import {
  ICorporateInformation,
  IIncomeCategory,
  IIncomeItem,
  IIncomeStructure,
  INatureOfBusiness,
} from '@Domains/NatureOfBusiness';
import { INCOME_SUBGROUP_CATEGORY } from 'src/Enum/IncomeStructure';
import {
  INatureOfBusinessFormValues,
  CorporateInformationFormValues,
  IIncomeStructureFormValues,
  IIncomeItemFormValues,
} from './Containers/NatureOfBusinessFormProvider/types';
import { INFO_CATEGORY_MASTER_DATA } from 'src/Enum/MasterData';
import { IUserProfile } from '@Domains/Profile';

const getFieldId = (data: IIncomeCategory): string => {
  return data.subGroup
    ? `${data.subGroup}-${data.sequence}`
    : (data?.infoCategoryMasterData?.id?.toString() as string);
};

const mapCorporateInformation = (
  corporateInformation: ICorporateInformation,
  userProfile?: IUserProfile
): CorporateInformationFormValues => {
  const { id, introduction, remark, completedSection } = corporateInformation;

  const symbol = userProfile?.E_COMPANY ?? '';
  const companyTh = userProfile?.N_COMPANY_THAI ?? '';
  const companyEn = userProfile?.N_COMPANY_ENG ?? '';

  return {
    id,
    introduction,
    remark,
    completedSection,
    thai: {
      companyTh: corporateInformation.companyTh
        ? corporateInformation.companyTh
        : companyTh,
      symbolTh: corporateInformation.symbolTh
        ? corporateInformation.symbolTh
        : symbol,
      addressTh: corporateInformation.addressTh,
      provinceTh: corporateInformation.provinceTh,
      zipcodeTh: corporateInformation.zipcodeTh,
      businessTypeTh: corporateInformation.businessTypeTh,
      faxTh: corporateInformation.faxTh,
      telephoneTh: corporateInformation.telephoneTh,
      registeredNumberTh: corporateInformation.registeredNumberTh,
      websiteTh: corporateInformation.websiteTh,
      emailTh: corporateInformation.emailTh,
    },
    english: {
      companyEn: corporateInformation.companyEn
        ? corporateInformation.companyEn
        : companyEn,
      symbolEn: corporateInformation.symbolEn
        ? corporateInformation.symbolEn
        : symbol,
      addressEn: corporateInformation.addressEn,
      provinceEn: corporateInformation.provinceEn,
      zipcodeEn: corporateInformation.zipcodeEn
        ? corporateInformation.zipcodeEn
        : corporateInformation.zipcodeTh,
      businessTypeEn: corporateInformation.businessTypeEn,
      faxEn: corporateInformation.faxEn
        ? corporateInformation.faxEn
        : corporateInformation.faxTh,
      telephoneEn: corporateInformation.telephoneEn
        ? corporateInformation.telephoneEn
        : corporateInformation.telephoneTh,
      registeredNumberEn: corporateInformation.registeredNumberEn
        ? corporateInformation.registeredNumberEn
        : corporateInformation.registeredNumberTh,
      websiteEn: corporateInformation.websiteEn
        ? corporateInformation.websiteEn
        : corporateInformation.websiteTh,
      emailEn: corporateInformation.emailEn
        ? corporateInformation.emailEn
        : corporateInformation.emailTh,
    },
  };
};

export const mapNatureOfBusinessToFormValues = (
  data: INatureOfBusiness,
  userProfile?: IUserProfile
): INatureOfBusinessFormValues => {
  return {
    ...data,
    corporateInformation: mapCorporateInformation(
      data?.corporateInformation,
      userProfile
    ),
    incomeStructure: mapIncomeStructureV2ToFormValues(data?.incomeStructure),
  };
};

const mapIncomeStructureV2ToFormValues = (
  incomeStructure: IIncomeStructure
): IIncomeStructureFormValues => {
  const { incomeItems, incomeItemPercents, ...values } = incomeStructure;

  const productIncomeInfo = INFO_CATEGORY_MASTER_DATA.PRODUCT_INCOME;
  const exportIncomeInfo = INFO_CATEGORY_MASTER_DATA.EXPORT_INCOME;
  const otherIncomeInfo = INFO_CATEGORY_MASTER_DATA.OTHER_INCOME;
  const otherSubgroup = [
    INCOME_SUBGROUP_CATEGORY.OTHER_OPERATION_INCOME,
    INCOME_SUBGROUP_CATEGORY.OTHER_NON_OPERATION_INCOME,
  ];

  const productIncomeItems = mapIncomeItemV2(incomeItems, productIncomeInfo);
  const exportIncomeItems = mapIncomeItemV2(incomeItems, exportIncomeInfo);
  const otherIncomeItems = mapIncomeItemV2(
    incomeItems,
    otherIncomeInfo,
    otherSubgroup
  );
  const productIncomePercentages = incomeItemPercents
    ? mapIncomeItemV2(incomeItemPercents, productIncomeInfo)
    : {};
  const exportIncomePercentages = incomeItemPercents
    ? mapIncomeItemV2(incomeItemPercents, exportIncomeInfo)
    : {};

  return {
    ...values,
    productIncomeItems,
    exportIncomeItems,
    otherIncomeItems,
    productIncomePercentages,
    exportIncomePercentages,
  };
};

const mapIncomeItemV2 = (
  incomeItems: IIncomeItem[],
  infoCategory: INFO_CATEGORY_MASTER_DATA,
  otherSubgroup?: string[]
): Record<string, IIncomeItemFormValues> =>
  incomeItems
    .filter((item) =>
      matchWithCategoryAndSubgroup(item, infoCategory, otherSubgroup)
    )
    .reduce((acc, incomeItem) => {
      const {
        asOfYear,
        asOfYesteryear,
        asOfYearBeforeYesteryear,
        incomeCategory,
        ...values
      } = incomeItem;

      acc[getFieldId(incomeCategory)] = {
        ...values,
        asOfYear: formatNumberToString(asOfYear),
        asOfYesteryear: formatNumberToString(asOfYesteryear),
        asOfYearBeforeYesteryear: formatNumberToString(
          asOfYearBeforeYesteryear
        ),
        incomeCategory: {
          ...incomeCategory,
          subGroup: incomeCategory.subGroup
            ? (incomeCategory.subGroup as INCOME_SUBGROUP_CATEGORY)
            : undefined,
        },
      };
      return acc;
    }, {} as Record<string, IIncomeItemFormValues>);

const matchWithCategoryAndSubgroup = (
  incomeItem: IIncomeItem,
  infoCategory: INFO_CATEGORY_MASTER_DATA,
  otherSubgroup?: string[]
): boolean => {
  const { incomeCategory } = incomeItem;

  return (
    incomeCategory.infoCategoryMasterData?.infoCategory === infoCategory ||
    incomeCategory.subGroup === infoCategory ||
    !!(otherSubgroup && otherSubgroup?.includes(incomeCategory.subGroup || ''))
  );
};

const formatNumberToString = (value?: number): string =>
  value !== null && value !== undefined ? value.toString() : '';
